/**
 * Call media capture service (Meet/Zoom-style).
 *
 * 1. Acquire mic/camera once under a user gesture (Start / Answer / Allow access).
 * 2. Hold live tracks so matrix-js-sdk getUserMedia reuses them (no second prompt).
 * 3. Keep iOS AudioSession on PlayAndRecord for the whole call epoch.
 * 4. Never treat silent prewarm failure as success — callers must gate place/answer.
 * 5. Camera re-acquire is video-only, serialized, and abort-safe so unmute cannot
 *    leak the camera or collide with a held microphone on iOS.
 */

import { persistDeviceMediaCapturePermission } from '../../utils/config/devicePermissionStorage';
import {
  acquireCallAudioSession,
  reassertCallAudioSession,
  releaseCallAudioSession,
} from './callAudioSessionPolicy';
import { createAbortError, createDeferred, createTimeoutError } from './callMediaAsync';
import {
  buildCallVideoConstraints,
  buildStableCallMediaConstraints,
  parseCaptureNeeds,
  parseRequestedVideoDeviceId,
  parseRequestedVideoFacingMode,
} from './callMediaConstraints';
import {
  buildCallMediaAccessError,
  classifyCallMediaError,
  CALL_MEDIA_ERROR_KIND,
} from './callMediaErrors';
import {
  findLiveVideoTrack,
  pruneEndedTracks,
  stopStreamTracksExcept,
  stopTrackQuietly,
  streamHasLiveAudio,
  streamHasLiveVideo,
  videoTrackSatisfiesRequest,
} from './callMediaTrackUtils';

const PATCH_FLAG = '__jhCallMediaCaptureServiceInstalled';
const RELEASE_DELAY_MS = 3000;
const CAMERA_REACQUIRE_ABORTED = 'Camera re-acquire aborted';
/** Backstop so a hung native GUM cannot freeze camera toggles for the rest of the session. */
const VIDEO_GUM_QUEUE_WATCHDOG_MS = 10000;

/** @type {((constraints: MediaStreamConstraints) => Promise<MediaStream>) | null} */
let originalGetUserMedia = null;

/** @type {MediaStream | null} */
let cachedStream = null;
/** @type {{ audio: boolean, video: boolean } | null} */
let cachedNeeds = null;
/** @type {Promise<MediaStream> | null} */
let inFlightCapture = null;
/** @type {{ audio: boolean, video: boolean } | null} */
let inFlightNeeds = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let releaseTimer = null;
/** @type {boolean} */
let captureEpochActive = false;
/** @type {string | null} */
let lastVideoDeviceId = null;
/** @type {string | null} */
let lastVideoFacingMode = null;
/** Serializes video re-acquire + abandoned-track cleanup so overlapping GUM cannot leak the camera. */
let videoWorkChain = Promise.resolve();
/** GUM tracks adopted into cache but not yet committed to a live call. */
const uncommittedVideoTracks = new Set();

function rememberVideoDeviceIdFromTrack(track) {
  if (track?.kind !== 'video') return;
  const settings = track.getSettings?.() ?? {};
  if (settings.deviceId) lastVideoDeviceId = settings.deviceId;
  if (settings.facingMode) lastVideoFacingMode = settings.facingMode;
}

function isTrackInCaptureCache(track) {
  if (!track || !cachedStream?.getTracks) return false;
  return cachedStream.getTracks().some((existing) => existing?.id === track.id);
}

function evictTrackFromCache(track) {
  if (!track || !cachedStream?.getTracks) return;
  const existing = cachedStream.getTracks().find((item) => item?.id === track.id);
  if (!existing) return;
  try {
    cachedStream.removeTrack(existing);
  } catch (_) { }
  cachedNeeds = {
    audio: streamHasLiveAudio(cachedStream),
    video: streamHasLiveVideo(cachedStream),
  };
  if (!(cachedStream.getTracks?.() ?? []).length) {
    cachedStream = null;
    cachedNeeds = null;
  }
}

function findLiveCachedVideoTrack() {
  if (cachedStream) pruneEndedTracks(cachedStream);
  return findLiveVideoTrack(cachedStream);
}

function enqueueCallVideoWork(task) {
  const run = videoWorkChain.then(task, task);
  videoWorkChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

function stopStreamWhenSettled(streamPromise) {
  void Promise.resolve(streamPromise).then(
    (stream) => stopStreamTracksExcept(stream, null),
    () => { }
  );
}

/**
 * Await native camera GUM, but release the video queue on abort / watchdog.
 * Native getUserMedia ignores AbortSignal; leftover tracks are stopped when it
 * later settles so a hung camera cannot block every future toggle.
 *
 * @param {Promise<MediaStream>} gumPromise
 * @param {AbortSignal} [signal]
 * @returns {Promise<MediaStream>}
 */
function awaitVideoGumOrReleaseQueue(gumPromise, signal) {
  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (settle, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdogId);
      signal?.removeEventListener('abort', onAbort);
      settle(value);
    };

    const watchdogId = setTimeout(() => {
      finish(reject, createTimeoutError('Camera getUserMedia watchdog'));
    }, VIDEO_GUM_QUEUE_WATCHDOG_MS);

    const onAbort = () => finish(reject, createAbortError(CAMERA_REACQUIRE_ABORTED));

    if (signal) {
      if (signal.aborted) {
        finish(reject, createAbortError(CAMERA_REACQUIRE_ABORTED));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }

    gumPromise.then(
      (stream) => finish(resolve, stream),
      (error) => finish(reject, error)
    );
  });
}

async function awaitInFlightCapture() {
  if (!inFlightCapture) return;
  try {
    await inFlightCapture;
  } catch (_) { }
}

function streamSatisfiesNeeds(stream, needs) {
  if (!stream || !needs) return false;
  if (needs.audio && !streamHasLiveAudio(stream)) return false;
  if (needs.video && !streamHasLiveVideo(stream)) return false;
  return true;
}

function needsCover(subset, superset) {
  if (!subset || !superset) return false;
  if (subset.audio && !superset.audio) return false;
  if (subset.video && !superset.video) return false;
  return true;
}

function buildStreamFromCache(stream, needs) {
  const next = new MediaStream();
  if (needs.audio) {
    (stream.getAudioTracks?.() ?? [])
      .filter((track) => track.readyState === 'live')
      .forEach((track) => next.addTrack(track));
  }
  if (needs.video) {
    (stream.getVideoTracks?.() ?? [])
      .filter((track) => track.readyState === 'live')
      .forEach((track) => next.addTrack(track));
  }
  return next.getTracks().length > 0 ? next : null;
}

function ensureAudioSessionForCapture() {
  acquireCallAudioSession();
  reassertCallAudioSession();
}

function getNativeGetUserMedia() {
  if (originalGetUserMedia) return originalGetUserMedia;
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return null;
  }
  return navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
}

function rememberCaptureStream(stream, needs) {
  if (!stream || !needs) return;

  cancelScheduledRelease();
  captureEpochActive = true;
  pruneEndedTracks(stream);

  if (!cachedStream || !streamSatisfiesNeeds(cachedStream, cachedNeeds)) {
    cachedStream = stream;
    cachedNeeds = needs;
    (stream.getVideoTracks?.() ?? []).forEach(rememberVideoDeviceIdFromTrack);
    return;
  }

  if (needs.video && !cachedNeeds.video) {
    cachedStream = stream;
    cachedNeeds = needs;
    (stream.getVideoTracks?.() ?? []).forEach(rememberVideoDeviceIdFromTrack);
  }
}

function adoptLiveTrackIntoCache(track) {
  if (!track || track.readyState !== 'live') return;

  cancelScheduledRelease();
  captureEpochActive = true;

  if (!cachedStream) {
    cachedStream = new MediaStream([track]);
    cachedNeeds = { audio: track.kind === 'audio', video: track.kind === 'video' };
    rememberVideoDeviceIdFromTrack(track);
    return;
  }

  pruneEndedTracks(cachedStream);
  cachedStream.getTracks()
    .filter((existing) => existing.kind === track.kind && existing.id !== track.id)
    .forEach((existing) => {
      uncommittedVideoTracks.delete(existing);
      try {
        cachedStream.removeTrack(existing);
      } catch (_) { }
      stopTrackQuietly(existing);
    });
  if (!cachedStream.getTrackById?.(track.id)) {
    try {
      cachedStream.addTrack(track);
    } catch (_) { }
  }
  cachedNeeds = {
    audio: Boolean(cachedNeeds?.audio) || streamHasLiveAudio(cachedStream),
    video: Boolean(cachedNeeds?.video) || streamHasLiveVideo(cachedStream),
  };
  rememberVideoDeviceIdFromTrack(track);
}

function cancelScheduledRelease() {
  if (releaseTimer) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
}

function clearCacheState({ stopTracks = false } = {}) {
  cancelScheduledRelease();

  if (stopTracks && cachedStream) {
    try {
      cachedStream.getTracks?.().forEach((track) => {
        try {
          track.stop();
        } catch (_) { }
      });
    } catch (_) { }
  }

  cachedStream = null;
  cachedNeeds = null;
  inFlightCapture = null;
  inFlightNeeds = null;
  lastVideoDeviceId = null;
  lastVideoFacingMode = null;
  uncommittedVideoTracks.clear();
}

/**
 * Always calls the native getUserMedia (never the patched wrapper).
 * @param {MediaStreamConstraints} constraints
 * @returns {Promise<MediaStream>}
 */
async function requestUserMediaWithAudioSessionRetry(constraints) {
  const nativeGum = getNativeGetUserMedia();
  if (!nativeGum) {
    throw Object.assign(new Error('Media devices are not available in this browser.'), {
      name: 'TypeError',
    });
  }

  const needs = parseCaptureNeeds(constraints);
  if (needs.audio) {
    ensureAudioSessionForCapture();
  }

  try {
    return await nativeGum(constraints);
  } catch (firstErr) {
    const classified = classifyCallMediaError(firstErr);
    if (classified.kind !== CALL_MEDIA_ERROR_KIND.AUDIO_SESSION || !needs.audio) {
      throw firstErr;
    }

    ensureAudioSessionForCapture();
    reassertCallAudioSession();
    return nativeGum(constraints);
  }
}

/**
 * Release cached capture after call teardown.
 * End PlayAndRecord immediately so the next incoming ringtone can use the speaker
 * (transient). Track stop stays delayed so SDK teardown does not race.
 */
export function releaseCallMediaCaptureCache() {
  cancelScheduledRelease();
  captureEpochActive = false;
  releaseCallAudioSession();
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    clearCacheState({ stopTracks: true });
  }, RELEASE_DELAY_MS);
}

/**
 * @param {'voice'|'video'|string} callKind
 * @returns {boolean}
 */
export function hasLiveCallMediaCapture(callKind) {
  const needs = parseCaptureNeeds(buildStableCallMediaConstraints(callKind));
  return Boolean(cachedStream && streamSatisfiesNeeds(cachedStream, needs));
}

/**
 * Acquire (or reuse) call mic/camera. Prefer calling from a user-gesture handler.
 *
 * @param {'voice'|'video'|string} callKind
 * @returns {Promise<
 *   | { ok: true, stream: MediaStream }
 *   | { ok: false, stream: null, error: ReturnType<typeof buildCallMediaAccessError> }
 * >}
 */
export async function acquireCallMediaCapture(callKind) {
  if (typeof navigator === 'undefined' || !getNativeGetUserMedia()) {
    return {
      ok: false,
      stream: null,
      error: buildCallMediaAccessError({
        name: 'TypeError',
        message: 'Media devices are not available in this browser.',
      }),
    };
  }

  const constraints = buildStableCallMediaConstraints(callKind);
  const needs = parseCaptureNeeds(constraints);

  if (cachedStream && streamSatisfiesNeeds(cachedStream, needs)) {
    ensureAudioSessionForCapture();
    const cached = buildStreamFromCache(cachedStream, needs);
    if (cached) {
      persistDeviceMediaCapturePermission('granted');
      return { ok: true, stream: cached };
    }
  }

  if (inFlightCapture && inFlightNeeds && needsCover(needs, inFlightNeeds)) {
    try {
      const stream = await inFlightCapture;
      if (streamSatisfiesNeeds(stream, needs)) {
        rememberCaptureStream(stream, inFlightNeeds);
        persistDeviceMediaCapturePermission('granted');
        const cached = buildStreamFromCache(stream, needs);
        if (cached) return { ok: true, stream: cached };
      }
    } catch (err) {
      return { ok: false, stream: null, error: buildCallMediaAccessError(err) };
    }
  }

  inFlightNeeds = needs;
  inFlightCapture = requestUserMediaWithAudioSessionRetry(constraints)
    .then((stream) => {
      rememberCaptureStream(stream, needs);
      persistDeviceMediaCapturePermission('granted');
      return stream;
    })
    .finally(() => {
      inFlightCapture = null;
      inFlightNeeds = null;
    });

  try {
    const stream = await inFlightCapture;
    return { ok: true, stream };
  } catch (err) {
    return { ok: false, stream: null, error: buildCallMediaAccessError(err) };
  }
}

/**
 * Gate for placeCall / answerCall — acquires if needed.
 * @param {'voice'|'video'|string} callKind
 */
export async function ensureCallMediaReady(callKind) {
  return acquireCallMediaCapture(callKind);
}

/**
 * Best-effort gesture prewarm. Prefer {@link acquireCallMediaCapture} when the
 * caller must know success/failure.
 * @param {'voice'|'video'|string} callKind
 * @returns {Promise<MediaStream | null>}
 */
export async function prewarmCallMediaCapture(callKind) {
  const result = await acquireCallMediaCapture(callKind);
  return result.ok ? result.stream : null;
}

export function isCallMediaCaptureEpochActive() {
  return captureEpochActive;
}

async function requestVideoOnlyStream(nativeGum, { deviceId, facingMode, signal } = {}) {
  const attempts = [];

  if (deviceId) {
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: buildCallVideoConstraints({ exactDeviceId: deviceId }),
      })
    );
    // iOS often rejects exact deviceId + min width/height; retry device only.
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: { deviceId: { exact: deviceId } },
      })
    );
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: { deviceId: { ideal: deviceId } },
      })
    );
  }

  if (facingMode) {
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: buildCallVideoConstraints({ facingMode }),
      })
    );
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: { facingMode: { ideal: facingMode } },
      })
    );
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: { facingMode },
      })
    );
  }

  // Unconstrained / front-default only when the caller did not name a camera.
  // Falling back to facingMode:user here is why flip kept opening the selfie cam.
  if (!deviceId && !facingMode) {
    attempts.push(() =>
      nativeGum({
        audio: false,
        video: buildCallVideoConstraints(),
      })
    );
    attempts.push(() => nativeGum({ audio: false, video: true }));
  }

  let lastError;
  for (const attempt of attempts) {
    if (signal?.aborted) {
      throw createAbortError(CAMERA_REACQUIRE_ABORTED);
    }
    try {
      const stream = await attempt();
      if (signal?.aborted) {
        stopStreamTracksExcept(stream, null);
        throw createAbortError(CAMERA_REACQUIRE_ABORTED);
      }
      const track = findLiveVideoTrack(stream);
      if (track && videoTrackSatisfiesRequest(track, deviceId, facingMode)) {
        return stream;
      }
      stopStreamTracksExcept(stream, null);
      lastError = Object.assign(new Error('Camera re-acquire returned no matching video track.'), {
        name: 'NotReadableError',
      });
    } catch (err) {
      if (err?.name === 'AbortError' || signal?.aborted) {
        throw err?.name === 'AbortError' ? err : createAbortError(CAMERA_REACQUIRE_ABORTED);
      }
      lastError = err;
    }
  }
  throw lastError || Object.assign(new Error('Camera re-acquire failed.'), {
    name: 'NotReadableError',
  });
}

/**
 * Re-acquire a live camera track without touching the held microphone.
 * After matrix-js-sdk mutes video it stops the camera track; a full
 * audio+video getUserMedia then fails on iOS because the mic is still in use.
 *
 * Abort unblocks the caller immediately. Native getUserMedia cannot be cancelled,
 * so leftover tracks are stopped when it later settles. The video queue does not
 * wait forever — abort and a watchdog release it so a hung camera cannot freeze
 * later toggles.
 *
 * A matching live cached track is reused. A live cache from a different
 * deviceId is stopped before the new GUM. New tracks are adopted into the
 * capture cache immediately so a concurrent acquire cannot open a second camera.
 *
 * @param {{ exactDeviceId?: string, facingMode?: string, signal?: AbortSignal }} [options]
 * @returns {Promise<MediaStreamTrack>}
 */
export async function acquireCallVideoTrack(options = {}) {
  const { exactDeviceId, facingMode, signal } = options;
  const caller = createDeferred();
  const abortError = () => createAbortError(CAMERA_REACQUIRE_ABORTED);

  const onAbort = () => caller.reject(abortError());
  if (signal) {
    if (signal.aborted) {
      caller.reject(abortError());
      return caller.promise;
    }
    signal.addEventListener('abort', onAbort, { once: true });
  }

  void enqueueCallVideoWork(async () => {
    try {
      if (signal?.aborted) {
        caller.reject(abortError());
        return;
      }

      await awaitInFlightCapture();
      if (signal?.aborted) {
        caller.reject(abortError());
        return;
      }

      const cachedVideo = findLiveCachedVideoTrack();
      const requestedDeviceId = exactDeviceId || null;
      const requestedFacing = facingMode || null;
      if (cachedVideo && videoTrackSatisfiesRequest(cachedVideo, requestedDeviceId, requestedFacing)) {
        caller.resolve(cachedVideo);
        return;
      }
      if (cachedVideo) {
        uncommittedVideoTracks.delete(cachedVideo);
        evictTrackFromCache(cachedVideo);
        stopTrackQuietly(cachedVideo);
      }

      const nativeGum = getNativeGetUserMedia();
      if (!nativeGum) {
        caller.reject(Object.assign(new Error('Media devices are not available in this browser.'), {
          name: 'TypeError',
        }));
        return;
      }

      const preferredDeviceId = requestedDeviceId || lastVideoDeviceId;
      const preferredFacing = requestedFacing || lastVideoFacingMode;
      const gumPromise = requestVideoOnlyStream(nativeGum, {
        deviceId: preferredDeviceId,
        facingMode: preferredFacing,
        signal,
      });
      let stream;
      try {
        stream = await awaitVideoGumOrReleaseQueue(gumPromise, signal);
      } catch (err) {
        stopStreamWhenSettled(gumPromise);
        caller.reject(err);
        return;
      }

      if (signal?.aborted) {
        stopStreamTracksExcept(stream, null);
        caller.reject(abortError());
        return;
      }

      const track = findLiveVideoTrack(stream);
      if (!track) {
        stopStreamTracksExcept(stream, null);
        caller.reject(Object.assign(new Error('Camera re-acquire returned no live video track.'), {
          name: 'NotReadableError',
        }));
        return;
      }

      stopStreamTracksExcept(stream, track);

      if (signal?.aborted) {
        stopTrackQuietly(track);
        caller.reject(abortError());
        return;
      }

      persistDeviceMediaCapturePermission('granted');
      rememberVideoDeviceIdFromTrack(track);
      adoptLiveTrackIntoCache(track);
      uncommittedVideoTracks.add(track);

      if (signal?.aborted || caller.settled) {
        releaseUnadoptedCallVideoTrack(track);
        caller.reject(abortError());
        return;
      }
      caller.resolve(track);
    } catch (err) {
      caller.reject(err);
    } finally {
      signal?.removeEventListener('abort', onAbort);
    }
  });

  return caller.promise;
}

/**
 * Stop a track that never made it onto a live call.
 * Reused cache hits are left running — the caller does not own those.
 * GUM tracks adopted early are evicted and stopped so a failed restore cannot leak the camera.
 *
 * @param {MediaStreamTrack | null | undefined} track
 */
export function releaseUnadoptedCallVideoTrack(track) {
  if (!track) return;
  const uncommitted = uncommittedVideoTracks.delete(track);
  if (uncommitted) {
    evictTrackFromCache(track);
  }
  if (track.readyState !== 'live') return;
  if (!uncommitted && isTrackInCaptureCache(track)) return;
  stopTrackQuietly(track);
}

/** Keep the capture cache in sync after a video track is attached to the live call. */
export function adoptLiveVideoTrackIntoCallCaptureCache(track) {
  uncommittedVideoTracks.delete(track);
  adoptLiveTrackIntoCache(track);
}

function serveCachedCaptureStream(needs, requestedVideoDeviceId, requestedFacingMode) {
  if (!cachedStream || !streamSatisfiesNeeds(cachedStream, needs)) return null;
  if (needs.video) {
    const cachedVideo = findLiveCachedVideoTrack();
    if (!cachedVideo) return null;
    // Only a requested deviceId can evict the live camera. The quality patch
    // adds facingMode:user as a default; treating that as a switch would put
    // the selfie cam back after a successful flip.
    if (
      requestedVideoDeviceId &&
      !videoTrackSatisfiesRequest(cachedVideo, requestedVideoDeviceId, requestedFacingMode)
    ) {
      return null;
    }
  }
  persistDeviceMediaCapturePermission('granted');
  if (needs.audio) ensureAudioSessionForCapture();
  return buildStreamFromCache(cachedStream, needs);
}

async function reacquireCachedVideoForRequest(requestedVideoDeviceId, requestedFacingMode, signal) {
  const cachedVideo = findLiveCachedVideoTrack();
  if (cachedVideo) {
    uncommittedVideoTracks.delete(cachedVideo);
    evictTrackFromCache(cachedVideo);
    stopTrackQuietly(cachedVideo);
  }

  const nativeGum = getNativeGetUserMedia();
  if (!nativeGum) {
    throw Object.assign(new Error('Media devices are not available in this browser.'), {
      name: 'TypeError',
    });
  }

  const stream = await requestVideoOnlyStream(nativeGum, {
    deviceId: requestedVideoDeviceId,
    facingMode: requestedFacingMode,
    signal,
  });
  const track = findLiveVideoTrack(stream);
  if (!track) {
    stopStreamTracksExcept(stream, null);
    throw Object.assign(new Error('Camera re-acquire returned no live video track.'), {
      name: 'NotReadableError',
    });
  }
  stopStreamTracksExcept(stream, track);
  persistDeviceMediaCapturePermission('granted');
  rememberVideoDeviceIdFromTrack(track);
  adoptLiveTrackIntoCache(track);
  return track;
}

function beginNativeCapture(constraints, needs) {
  if (inFlightCapture && inFlightNeeds && needsCover(needs, inFlightNeeds)) {
    return inFlightCapture.then((stream) => {
      if (streamSatisfiesNeeds(stream, needs)) {
        rememberCaptureStream(stream, inFlightNeeds);
        persistDeviceMediaCapturePermission('granted');
        const cached = buildStreamFromCache(stream, needs);
        if (cached) return cached;
      }
      if (needs.audio) ensureAudioSessionForCapture();
      return originalGetUserMedia(constraints);
    });
  }

  inFlightNeeds = needs;
  if (needs.audio) ensureAudioSessionForCapture();

  inFlightCapture = requestUserMediaWithAudioSessionRetry(constraints)
    .then((stream) => {
      rememberCaptureStream(stream, needs);
      persistDeviceMediaCapturePermission('granted');
      return stream;
    })
    .finally(() => {
      inFlightCapture = null;
      inFlightNeeds = null;
    });

  return inFlightCapture;
}

function installGetUserMediaCaptureService() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
  const mediaDevices = navigator.mediaDevices;
  if (mediaDevices[PATCH_FLAG]) return;

  originalGetUserMedia = mediaDevices.getUserMedia.bind(mediaDevices);
  mediaDevices[PATCH_FLAG] = true;

  mediaDevices.getUserMedia = function getUserMediaWithCaptureService(constraints) {
    const needs = parseCaptureNeeds(constraints);
    const requestedVideoDeviceId = parseRequestedVideoDeviceId(constraints);
    const requestedFacingMode = parseRequestedVideoFacingMode(constraints);

    const capture = async () => {
      const cached = serveCachedCaptureStream(needs, requestedVideoDeviceId, requestedFacingMode);
      if (cached) return cached;

      // After hangup, do not start new capture prompts from SDK retries.
      if (!captureEpochActive && !cachedStream && releaseTimer) {
        throw Object.assign(new Error('Call media capture was released'), {
          name: 'InvalidStateError',
        });
      }

      // Video-only GUM while the mic is held (iOS cannot open audio+video again).
      // Switch cameras only when the caller named a different deviceId.
      // Opening video onto a live mic is also video-only.
      const cachedVideo = findLiveCachedVideoTrack();
      const deviceMismatch = Boolean(
        needs.video &&
        requestedVideoDeviceId &&
        cachedVideo &&
        !videoTrackSatisfiesRequest(cachedVideo, requestedVideoDeviceId, requestedFacingMode)
      );
      const needVideoOnLiveMic = Boolean(
        needs.video && streamHasLiveAudio(cachedStream) && !cachedVideo
      );
      if (deviceMismatch || needVideoOnLiveMic) {
        await reacquireCachedVideoForRequest(
          requestedVideoDeviceId,
          requestedFacingMode ||
            (!requestedVideoDeviceId || requestedVideoDeviceId === lastVideoDeviceId
              ? lastVideoFacingMode
              : null)
        );
        // Serve the camera we just opened — do not re-filter by deviceId
        // (iPhone back sensors can report a different id than enumerateDevices).
        const next = serveCachedCaptureStream(needs);
        if (next) return next;
      }

      return beginNativeCapture(constraints, needs);
    };

    // Video cache hits share this queue so SDK GUM cannot receive a track that
    // acquireCallVideoTrack is about to evict/stop.
    if (needs.video) {
      return enqueueCallVideoWork(capture);
    }
    return Promise.resolve().then(capture);
  };
}

installGetUserMediaCaptureService();
